"use client";

import {
  createContext,
  useContext,
  useState,
  Dispatch,
  SetStateAction,
} from "react";
import { useMerchant } from "@/app/providers/merchantProvider";
import { getFromLocalStorage } from "@/utils/localStorageUtils";
import { LocalStorageKey } from "@/utils/storageKeys";
import { GiftCard } from "@/dto/giftCard";
import { RetailCart } from "@/models/cart/retailCart";
import { ServiceCart } from "@/models/cart/serviceCart";
import useMediaQuery from "@/utils/useMediaQuery";
import { useCookies } from "react-cookie";

interface CartDrawerContextType {
  isRetailCartOpen: boolean;
  setIsRetailCartOpen: Dispatch<SetStateAction<boolean>>;
  isRestaurantCartOpen?: boolean;
  setIsRestaurantCartOpen: Dispatch<SetStateAction<boolean | undefined>>;
  isServiceSummaryOpen: boolean;
  setIsServiceSummaryOpen: Dispatch<SetStateAction<boolean>>;
  toggleCartDrawer: () => void;
  openCartDrawer: () => void;
  isHandlingCartDrawer: boolean;
  setIsHandlingCartDrawer: Dispatch<SetStateAction<boolean>>;
  isProceedingToCheckout: boolean;
  setIsProceedingToCheckout: Dispatch<SetStateAction<boolean>>;
}

const CartDrawerContext = createContext<CartDrawerContextType | undefined>(
  undefined
);

export const CartDrawerProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const isMobile = useMediaQuery("(max-width: 640px)");
  const [cookies] = useCookies(["source"]);
  const { source } = cookies;
  const isCmaApp = source === "cma";
  const { merchant } = useMerchant();
  const [isRetailCartOpen, setIsRetailCartOpen] = useState(false);
  const [isRestaurantCartOpen, setIsRestaurantCartOpen] = useState<
    boolean | undefined
  >(false);
  const [isServiceSummaryOpen, setIsServiceSummaryOpen] = useState(false);
  const [isHandlingCartDrawer, setIsHandlingCartDrawer] = useState(false);
  const [isProceedingToCheckout, setIsProceedingToCheckout] = useState(false);

  // Helper function to determine which drawer logic to apply
  const determineDrawerAction = (action: "open" | "toggle") => {
    setIsProceedingToCheckout(false);
    const storedGiftCard = getFromLocalStorage<GiftCard | null>(
      LocalStorageKey.GIFT_CARD,
      null
    );
    const retailCart = RetailCart.getItems();
    const serviceCart = ServiceCart.getItems();

    type DrawerTarget = {
      setter:
        | Dispatch<SetStateAction<boolean>>
        | Dispatch<SetStateAction<boolean | undefined>>;
      currentValue: boolean | undefined;
    };

    const target: DrawerTarget = (() => {
      if (
        merchant.isService &&
        !merchant.isHybrid &&
        !merchant.hasOnlyRetailItems &&
        !storedGiftCard
      ) {
        return {
          setter: setIsServiceSummaryOpen,
          currentValue: isServiceSummaryOpen,
        };
      } else if (merchant.isRestaurant) {
        return {
          setter: setIsRestaurantCartOpen,
          currentValue: isRestaurantCartOpen,
        };
      } else if (
        merchant.isHybrid &&
        (retailCart.length >= 1 || isRetailCartOpen)
      ) {
        return { setter: setIsRetailCartOpen, currentValue: isRetailCartOpen };
      } else if (
        merchant.isHybrid &&
        (serviceCart?.length >= 1 || (!retailCart.length && !storedGiftCard))
      ) {
        return {
          setter: setIsServiceSummaryOpen,
          currentValue: isServiceSummaryOpen,
        };
      } else {
        return { setter: setIsRetailCartOpen, currentValue: isRetailCartOpen };
      }
    })();

    target.setter(action === "open" ? true : !target.currentValue);
  };

  const toggleCartDrawer = () => {
    setIsHandlingCartDrawer(true);
    determineDrawerAction("toggle");
  };

  const openCartDrawer = () => {
    if (isMobile && !isCmaApp) return;
    setIsHandlingCartDrawer(true);
    determineDrawerAction("open");
  };

  return (
    <CartDrawerContext.Provider
      value={{
        isRetailCartOpen,
        setIsRetailCartOpen,
        isRestaurantCartOpen,
        setIsRestaurantCartOpen,
        isServiceSummaryOpen,
        setIsServiceSummaryOpen,
        toggleCartDrawer,
        openCartDrawer,
        isHandlingCartDrawer,
        setIsHandlingCartDrawer,
        isProceedingToCheckout,
        setIsProceedingToCheckout,
      }}
    >
      {children}
    </CartDrawerContext.Provider>
  );
};

export const useCartDrawer = () => {
  const context = useContext(CartDrawerContext);
  if (context === undefined) {
    throw new Error("useCartDrawer must be used within a CartDrawerProvider");
  }
  return context;
};
